import { sportLabel } from '../Util/sportsCatalog';

/*
 * Livelli, skill e trofei Move nella scheda del player e del junior (segnalazione del 04/11/2026). I dati arrivano da
 * GET /sports/levels, /sports/skills, /moveGoals/rewards e /moveGoals/live/rewards; qui solo come mostrarli.
 * Contano solo le certificazioni fatte da un maestro: quelle che il player si è dato da solo (`autoCertified`) no.
 */

const VUOTO = '—';

const data = (iso) => {
    if (!iso) return VUOTO;
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? VUOTO : d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const certificataDalMaestro = (voce) => !!voce?.certification && voce.autoCertified !== true;

const maestro = (voce) => voce?.certification?.certifier?.name || voce?.certification?.certifier?.username || VUOTO;

/** Il nome del livello come lo usa quello sport, es. «INTERMEDIO 4.3» (categoria e livello). */
export const nomeLivello = (livello) =>
    [livello?.category?.name, livello?.name].filter(Boolean).join(' ') || VUOTO;

/**
 * Il livello più alto certificato da un maestro per ogni sport, da GET /sports/levels: { [sportType]: { livello, data,
 * maestro } }. Uno sport senza certificazioni non c'è: in tabella vale «0».
 */
export const livelliCertificati = (risposta) => {
    const perSport = {};
    for (const sport of Array.isArray(risposta) ? risposta : []) {
        const migliore = (sport.levels || [])
            .filter(certificataDalMaestro)
            .sort((a, b) => (b.sublevelRank ?? 0) - (a.sublevelRank ?? 0))[0];
        if (migliore) {
            perSport[sport.sportType] = {
                livello: nomeLivello(migliore),
                data: data(migliore.certification.certifiedAt),
                maestro: maestro(migliore),
            };
        }
    }
    return perSport;
};

/** Le righe della tabella «Sport praticati»: livello certificato, «0» se manca, con data e maestro. */
export const righeSportPraticati = (sportPraticati, livelli, etichettaRuolo) =>
    (sportPraticati || []).map((s) => {
        const certificato = livelli[s.sportType];
        return {
            id: s.sportType,
            sport: sportLabel(s.sportType),
            level: certificato ? certificato.livello : '0',
            certifiedAt: certificato ? certificato.data : VUOTO,
            coach: certificato ? certificato.maestro : VUOTO,
            role: etichettaRuolo(s.sportRole),
            staff: s.isGuru ? 'Sì' : 'No',
        };
    });

/**
 * Le skill certificate da un maestro, divise per sport (GET /sports/skills restituisce tutto il catalogo: le
 * certificate sono quelle con `certification`). Sport senza skill certificate non compaiono.
 */
export const skillPerSport = (risposta) =>
    (Array.isArray(risposta) ? risposta : [])
        .map((sport) => ({
            sportType: sport.sportType,
            sport: sportLabel(sport.sportType),
            righe: (sport.skills || [])
                .filter(certificataDalMaestro)
                .map((skill) => ({
                    id: skill.sportSkillId,
                    skill: [skill.category?.name, skill.name].filter((x, i, tutte) => x && tutte.indexOf(x) === i).join(' › '),
                    value: `${Math.round(skill.sportSkillValue ?? 0)}%`,
                    certifiedAt: data(skill.certification.certifiedAt),
                    coach: maestro(skill),
                })),
        }))
        .filter((sport) => sport.righe.length > 0);

export const TROFEI = [
    { tipo: 'PLATINUM', nome: 'Platino' },
    { tipo: 'GOLD', nome: 'Oro' },
    { tipo: 'SILVER', nome: 'Argento' },
    { tipo: 'BRONZE', nome: 'Bronzo' },
];

const nomeTrofeo = (tipo) => TROFEI.find((t) => t.tipo === tipo)?.nome || VUOTO;

/** «GLOBAL» sono i traguardi che valgono per tutti gli sport (per esempio i passi). */
const nomeSport = (sportType) => (sportType === 'GLOBAL' ? 'Tutti gli sport' : sportLabel(sportType));

/** I trofei vinti nella stagione divisi per sport, da GET /moveGoals/rewards; con l'ultimo vinto. */
export const trofeiPerSport = (vinti) => {
    const perSport = new Map();
    let ultimo = null;
    for (const t of Array.isArray(vinti) ? vinti : []) {
        const riga = perSport.get(t.sportType) || { id: t.sportType, sport: nomeSport(t.sportType), PLATINUM: 0, GOLD: 0, SILVER: 0, BRONZE: 0 };
        if (t.trophyType in riga) riga[t.trophyType] += t.reward?.trophies ?? 1;
        perSport.set(t.sportType, riga);
        const quando = t.reward?.timestamp;
        if (quando && (!ultimo || new Date(quando) > new Date(ultimo))) ultimo = quando;
    }
    return { righe: [...perSport.values()].sort((a, b) => a.sport.localeCompare(b.sport, 'it')), ultimo };
};

const PERIODI = { DAY: 'Giorno', WEEK: 'Settimana', MONTH: 'Mese', TRIMESTER: 'Trimestre', SEMESTER: 'Semestre', YEAR: 'Anno' };

/**
 * I traguardi del periodo in corso non ancora vinti, da GET /moveGoals/live/rewards: avanzamento in percentuale
 * (valore raggiunto su soglia), il più vicino al trofeo per primo. L'unità di valore e soglia dipende dal traguardo.
 */
export const traguardiInCorso = (incorso) =>
    (Array.isArray(incorso) ? incorso : [])
        .filter((t) => !t.reward && t.threshold > 0)
        .map((t) => ({
            id: `${t.moveGoalId}-${t.period}`,
            sport: nomeSport(t.sportType),
            goal: t.description || VUOTO,
            period: PERIODI[t.periodType] || VUOTO,
            progress: Math.min(99, Math.floor(((t.value ?? 0) / t.threshold) * 100)),
            trophy: nomeTrofeo(t.trophyType),
        }))
        .sort((a, b) => b.progress - a.progress);

export const formatoData = data;
